import { Log } from '../../AuxillaryService/Log';
import { Permission, platform } from '../../Platform';

import { WidgetWithScheduler } from '../WidgetWithScheduler';
import { WidgetType } from '../types';
import {
    NwsRadarSettings,
    WeatherRadarWidgetConfiguration,
    WeatherRadarWidgetConfigurationDetails,
    ZodWeatherRadarWidgetConfigurationDetails,
} from './types';
import './weatherradar.scss';

export class WeatherRadarWidget extends WidgetWithScheduler {
    type: WidgetType.WEATHER_RADAR;

    declare config: WeatherRadarWidgetConfigurationDetails;

    private readonly baseIframeUrl: string = 'https://radar.weather.gov/?settings=v1_';

    private readonly iframeContainerClassName: string = 'weather-radar-map-section';

    private readonly iframeClassName: string = 'nws-weather-radar-iframe';

    private readonly hasLaxNetSecurity = platform().hasPermission(Permission.RELAXED_NET_SECURITY);

    #zoom: number;

    #county: boolean;

    #state: boolean;

    #artcc: boolean;

    #cwa: boolean;

    #rfc: boolean;

    #latitude: number;

    #longitude: number;

    #location: string;

    #iframeSettings: NwsRadarSettings;

    #coordinates: number[];

    #iframeElement: HTMLIFrameElement | null;

    #iframeContainer: HTMLElement | null;

    constructor(widgetConfiguration: WeatherRadarWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);
        this.schedulerRunInterval = 3;
        this.type = WidgetType.WEATHER_RADAR;
        this.#zoom = this.config.zoom;
        this.#county = this.config.county;
        this.#state = this.config.state;
        this.#artcc = this.config.artcc;
        this.#cwa = this.config.cwa;
        this.#rfc = this.config.rfc;
        this.#latitude = this.config.latitude;
        this.#longitude = this.config.longitude;
        this.#location = this.config.location;
        this.#coordinates = [this.#longitude, this.#latitude];
        this.#iframeSettings = {
            agenda: {
                id: 'weather',
                center: this.#coordinates,
                location: this.#coordinates,
                zoom: this.#zoom,
            },
            cwa: this.#cwa,
            county: this.#county,
            base: this.config.base_layer,
            state: this.#state,
            artcc: this.#artcc,
            rfc: this.#rfc,
            animating: false,
            menu: false,
            shortFusedOnly: false,
            opacity: {
                alerts: 0.8,
                local: 0.6,
                localStation: 0.8,
                national: 0.6,
            },
        };
        this.#iframeElement = null;
        this.#iframeContainer = null;
    }

    protected validateConfigurationDetails(
        weatherRadarWidgetConfigurationDetails: WeatherRadarWidgetConfigurationDetails
    ): WeatherRadarWidgetConfigurationDetails {
        return ZodWeatherRadarWidgetConfigurationDetails.parse(
            weatherRadarWidgetConfigurationDetails
        );
    }

    async stop(): Promise<void> {
        this.removeIframeAndSection();
        await super.stop();
    }

    async getReadyForDisplay(): Promise<void> {
        this.addIframeAndSectionToDOM();
    }

    /**
     * Attempts to add section containing iframe map
     * to DOM.
     */
    private addIframeAndSectionToDOM(): void {
        if (!this.isIframeContentInDOM()) {
            this.#iframeContainer = document.createElement('section');
            this.#iframeContainer.className = this.iframeContainerClassName;
            const stringifiedSettings: string = JSON.stringify(this.#iframeSettings);
            this.wrapperElement.appendChild(this.#iframeContainer);

            const encodedData: string = btoa(stringifiedSettings);

            this.#iframeElement = document.createElement('iframe');
            this.#iframeElement.id = `${this.name}_${this.id}_${this.#location}`;
            this.#iframeElement.src = `${this.baseIframeUrl}${encodedData}`;
            this.#iframeElement.className = this.iframeClassName;
            this.#iframeContainer.appendChild(this.#iframeElement);
        }
    }

    /**
     * Attempts to remove section containing iframe map
     * from DOM.
     */
    private removeIframeAndSection(): void {
        if (this.isIframeContentInDOM() && this.#iframeElement && this.#iframeContainer) {
            this.#iframeElement.remove();
            this.#iframeContainer.remove();
            this.#iframeElement = null;
            this.#iframeContainer = null;
        }
    }

    /**
     * Returns a boolean determining whether
     * the weather iframe content is present in the DOM.
     */
    private isIframeContentInDOM(): boolean {
        const isWrapperInDOM = this.wrapperElement.querySelector(
            `.${this.iframeContainerClassName}`
        );
        const isIframeInDOM = this.wrapperElement.querySelector(`.${this.iframeClassName}`);
        return isIframeInDOM !== null && isWrapperInDOM !== null;
    }

    schedulerAction(): void {
        if (!this.hasLaxNetSecurity) {
            Log.Warning(
                `${this.name}: Insufficient permissions to play Weather Radar`,
                null,
                __fileline
            );
            return;
        }
        if (this.isIframeContentInDOM() && this.#iframeElement) {
            const radarFrameAdvanceButton =
                this.#iframeElement.contentWindow?.document.querySelector<HTMLDivElement>(
                    '.button-stepfwd'
                );

            if (!radarFrameAdvanceButton) {
                Log.Error(
                    `${this.name}: Failed to find radar frame advance button`,
                    null,
                    __fileline
                );
                return;
            }
            radarFrameAdvanceButton.click();
            return;
        }

        Log.Warning(`${this.name}: Radar iframe element is not present`, null, __fileline);
    }
}
